import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import ArrowDown from "../../svg/ArrowDown";
import { PnoeMeasurementStatus } from "../../entities/pnoeMeasurement";
import { getStatusToneColors, StatusTone } from "../../utils/labPanelStatus";
import {
  MeasurementActionKind,
  getMeasurementActions,
} from "../../utils/pnoeMeasurementStatus";

/**
 * Status switcher on the PNOĒ test detail header (Figma nodes 16827:67272 /
 * 16886:68540).
 *
 * The same interaction as `PrescriptionStatusSelect` — a status pill that
 * opens a menu — over the measurement's own transitions rather than
 * active/inactive.
 *
 * **The menu lists only where the test can actually go**, which is what
 * `getMeasurementActions` already resolves:
 *
 * - `in_progress` → Completed or Invalidated
 * - `completed` → Invalidated
 * - `invalidated` → Completed
 *
 * So the current status is never among the options, and `In progress` is never
 * an option at all because nothing transitions back into it. The pill above
 * the menu is what says where the test stands.
 *
 * The pill shows the *composite* label ("Completed · Report ready"), since the
 * status a user reads is the measurement status plus the report status, while
 * the options address the measurement half alone.
 */

interface StatusOption {
  /** The transition to run when picked. */
  action: MeasurementActionKind;
  /** The measurement status this option lands on. */
  status: Extract<PnoeMeasurementStatus, "completed" | "invalidated">;
  label: string;
  tone: StatusTone;
}

const OPTIONS: StatusOption[] = [
  {
    action: "complete",
    status: "completed",
    label: "Completed",
    tone: "success",
  },
  {
    action: "invalidate",
    status: "invalidated",
    label: "Invalidated",
    tone: "danger",
  },
];

interface PnoeStatusSelectProps {
  /** The measurement's own status — decides which transitions are offered. */
  status: PnoeMeasurementStatus | null | undefined;
  /** Composite label for the pill, e.g. "Completed · Report ready". */
  label: string;
  /** Tone for the pill, from the composite status. */
  tone: StatusTone;
  onSelect: (action: MeasurementActionKind) => void;
  /** True while a transition is in flight — the menu stays shut. */
  disabled?: boolean;
  /**
   * Render the pill alone, with no chevron and no menu. For readers who may
   * not move the test — a customer looking at their own test — the status is
   * information, not a control.
   */
  readOnly?: boolean;
}

const PnoeStatusSelect: React.FC<PnoeStatusSelectProps> = ({
  status,
  label,
  tone,
  onSelect,
  disabled = false,
  readOnly = false,
}) => {
  const theme: Theme = useTheme();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on an outside click, same as PrescriptionStatusSelect. The listener
  // is attached a tick late so the click that opened the menu does not shut it.
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  // Only the transitions the measurement actually allows, so the menu never
  // offers the status the test is already in.
  const options = useMemo(() => {
    const allowed = getMeasurementActions(status).map((action) => action.kind);
    return OPTIONS.filter((option) => allowed.includes(option.action));
  }, [status]);

  const handleOptionClick = (option: StatusOption) => {
    setOpen(false);
    onSelect(option.action);
  };

  if (readOnly) {
    return (
      <Box sx={styles.root}>
        <Box sx={styles.pill(theme, tone)} aria-label={`Status: ${label}`}>
          <Box sx={styles.triggerLabel(theme)}>{label}</Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={styles.root}>
      <Box
        sx={styles.trigger(theme, tone, disabled)}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((wasOpen) => !wasOpen);
        }}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Box sx={styles.triggerLabel(theme)}>{label}</Box>
        <Box sx={styles.chevron(open)}>
          <ArrowDown width={16} height={16} />
        </Box>
      </Box>

      {open && (
        <Box sx={styles.menu(theme)} role="listbox">
          {options.map((option) => (
            <Box
              key={option.status}
              sx={styles.option(theme)}
              onClick={() => handleOptionClick(option)}
              role="option"
              aria-selected={false}
            >
              <Box sx={styles.dot(theme, option.tone)} />
              {option.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PnoeStatusSelect;

const styles = {
  root: {
    position: "relative",
    flexShrink: 0,
  },
  // The pill itself, shared by the trigger and the read-only rendering.
  pill: (theme: Theme, tone: StatusTone) => {
    const colors = getStatusToneColors(tone, theme);
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: "10px",
      padding: "4px 12px",
      borderRadius: "8px",
      backgroundColor: colors.bg,
      border: `1px solid ${colors.border}`,
      whiteSpace: "nowrap",
    };
  },
  trigger: (theme: Theme, tone: StatusTone, disabled: boolean) => ({
    ...styles.pill(theme, tone),
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.6 : 1,
    transition: "opacity 0.2s",
    "&:hover": disabled ? {} : { opacity: 0.8 },
  }),
  triggerLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  chevron: (open: boolean) => ({
    display: "flex",
    alignItems: "center",
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 200ms",
  }),
  menu: (theme: Theme) => ({
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    minWidth: "160px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "5px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    boxShadow: "var(--box-shadow-general)",
    overflow: "hidden",
    zIndex: 1000,
  }),
  option: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: "30px",
    padding: "0 12px",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "19.5px",
    whiteSpace: "nowrap",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    // No selected state: the current status is never offered, so every row is
    // a move the test can make.
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    "&:hover": {
      backgroundColor: theme.colorTokens.N[40],
    },
  }),
  dot: (theme: Theme, tone: StatusTone) => ({
    width: "8px",
    height: "8px",
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: getStatusToneColors(tone, theme).border,
  }),
};
